import { Button, Card, Col, Divider, Row } from "antd";
import { useContext, useEffect, useState, useCallback, useMemo } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { IDeliveryWorker } from "../../../models/delivery-workers/response";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import TripContext from "../../../context/trip/context";
import TripContextProvider from "../../../context/trip/provider";
import { ITripRequest } from "../../../models/trip/request";
import { TripStatus } from "../../../models/trip/response";
import { IOrderDetails } from "../../../models/order/response";
import { IStore } from "../../../models/store/response";
import EndPoints from "../../../services/end-points";
import { execute } from "../../../utils/helpers/execute";
import FieldBuilder from "../../form-components/field-builder";
import Spin from "../../general/antd/spin";
import TripRoutePlanner, { ITripRoutePlannerOrder } from "../trip-route-planner";
import styles from "./style.module.scss";
import RefreshBtn from "../../general/table-components/actions/refresh-btn";

const Form = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { details, loading, actions } = useContext(TripContext);
  const { id } = useParams();

  useEffect(() => {
    if (id) {
      actions.getDetails(Number(id));
    } else {
      actions.reset();
    }
  }, [id]);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [driversWithStoreData, setDriversWithStoreData] = useState<IDeliveryWorker[]>([]);
  const [stores, setStores] = useState<any[]>([]);
  const [storesWithLocation, setStoresWithLocation] = useState<IStore[]>([]);
  const [classifications, setClassifications] = useState<any[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<number | undefined>(undefined);
  const [selectedClassificationId, setSelectedClassificationId] = useState<number | undefined>(undefined);
  const [selectedStatus, setSelectedStatus] = useState<TripStatus | undefined>(undefined);
  const [routePlannerOrders, setRoutePlannerOrders] = useState<ITripRoutePlannerOrder[]>([]);
  const [unassignedOrdersWithLocation, setUnassignedOrdersWithLocation] = useState<IOrderDetails[]>([]);

  const methods = useForm<ITripRequest>({
    defaultValues: {
      driverId: undefined,
      orderIds: [],
    },
  });

  const { handleSubmit, reset } = methods;
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [loadingCreateAndStart, setLoadingCreateAndStart] = useState(false);
  const [loadingStartTrip, setLoadingStartTrip] = useState(false);
  const [loadingCancelTrip, setLoadingCancelTrip] = useState(false);
  const [refreshingData, setRefreshingData] = useState(false);
  const [loadingStores, setLoadingStores] = useState(false);
  const [loadingClassifications, setLoadingClassifications] = useState(false);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [unassignedOrders, setUnassignedOrders] = useState<any[]>([]);

  const fetchStores = async () => {
    setLoadingStores(true);
    try {
      const response = await EndPoints.store.getAllStores({
        light: false,
        perPage: -1,
        filters: [
          {
            name: "isNamlieah",
            value: 1,
            operation: "eq",
          }
        ]
      });
      setStoresWithLocation(response.data.data);
      setStores(
        response.data.data.map((store: any) => ({
          label: store.name,
          value: store.id,
        }))
      );
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingStores(false);
    }
  };

  const fetchClassifications = async () => {
    setLoadingClassifications(true);
    try {
      const response = await EndPoints.driverClassification.getAllDriverClassifications({
        light: true,
        perPage: -1,
      });
      setClassifications(
        response.data.data.map((classification: any) => ({
          label: classification.name,
          value: classification.id,
        }))
      );
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingClassifications(false);
    }
  };

  const fetchDrivers = async (storeId?: number, classificationId?: number) => {
    setLoadingDrivers(true);
    try {
      const filters: any[] = [
        {
          name: "status",
          value: 1,
          operation: "eq",
        },
        {
          name: "statusWorker",
          value: 1,
          operation: "eq",
        },
      ];

      if (storeId) {
        filters.push({
          name: "storeId",
          value: storeId,
          operation: "eq",
        });
      }

      if (classificationId) {
        filters.push({
          name: "driverClassificationId",
          value: classificationId,
          operation: "eq",
        });
      }

      const response = await EndPoints.deliveryWorkers.getAllDeliveryWorkers({
        light: false,
        perPage: -1,
        filters
      });
      const driversData = response.data.data;
      setDriversWithStoreData(driversData);
      setDrivers(
        driversData.map((driver: IDeliveryWorker) => ({
          label: `${driver.user?.firstName || ''} ${driver.user?.lastName || ''}`.trim() || `Driver ${driver.id}`,
          value: driver.id,
        }))
      );
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingDrivers(false);
    }
  };

  const fetchUnassignedOrders = async () => {
    setLoadingOrders(true);
    try {
      const response = await EndPoints.trip.getUnassignedOrders({
        light: false,
        perPage: -1,
      });
      setUnassignedOrdersWithLocation(response.data.data);
      setUnassignedOrders(
        response.data.data.map((order: any) => ({
          label: `${order.number} - ${order.client?.firstName} ${order.client?.lastName}`,
          value: order.id,
        }))
      );
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingOrders(false);
    }
  };


  const refreshDropdownData = async () => {
    setRefreshingData(true);
    try {
      await Promise.all([
        fetchStores(),
        fetchClassifications(),
        fetchDrivers(selectedStoreId, selectedClassificationId),
        fetchUnassignedOrders()
      ]);
    } finally {
      setRefreshingData(false);
    }
  };

  useEffect(() => {
    fetchStores();
    fetchClassifications();
    fetchUnassignedOrders();
    // Note: fetchDrivers is called by the useEffect below that watches store/classification filters
  }, []);

  useEffect(() => {
    // This runs on mount (with undefined values) and whenever filters change
    fetchDrivers(selectedStoreId, selectedClassificationId);
  }, [selectedStoreId, selectedClassificationId]);

  useEffect(() => {
    if (id && details) {
      reset({
        driverId: details.driverId,
        orderIds: details.orders?.map((order) => order.id) || [],
        status: details.status,
        reason: details.cancellationReason,
      });
      setSelectedStatus(details.status);

      // Ensure the current driver is in the dropdown options when editing
      if (details.driver && details.driverId) {
        // Driver name can be directly on driver object or nested under user
        let driverName = '';
        if (details.driver.firstName || details.driver.lastName) {
          driverName = `${details.driver.firstName || ''} ${details.driver.lastName || ''}`.trim();
        } else if (details.driver.user) {
          driverName = `${details.driver.user.firstName || ''} ${details.driver.user.lastName || ''}`.trim();
        }
        if (!driverName) {
          driverName = `Driver ${details.driverId}`;
        }

        setDrivers((prevDrivers) => {
          // Check if driver is already in the list
          const exists = prevDrivers.some((d) => d.value === details.driverId);
          if (!exists) {
            return [{ label: driverName, value: details.driverId }, ...prevDrivers];
          }
          return prevDrivers;
        });
      }
    }
  }, [details, id]);

  // Initialize route planner orders from details
  // Handle both client object (from unassigned orders) and clientName string (from trip orders)
  useEffect(() => {
    if (details?.orders) {
      const plannerOrders: ITripRoutePlannerOrder[] = details.orders.map((order: any) => {
        // Build client object from clientName if client object is not available
        let client = order.client;
        if (!client && order.clientName) {
          // Split clientName into firstName and lastName
          const nameParts = order.clientName.trim().split(' ');
          client = {
            firstName: nameParts[0] || '',
            lastName: nameParts.slice(1).join(' ') || '',
          };
        }

        return {
          id: order.id,
          number: order.number,
          status: order.status,
          sequence: order.pivot?.sequence || 0,
          distanceToNext: order.pivot?.distanceToNext || 0,
          client,
          clientAddress: order.clientAddress,
          location: order.location,
        };
      });
      setRoutePlannerOrders(plannerOrders);
    }
  }, [details]);

  // Watch the driverId field to get selected driver's store
  const selectedDriverId = useWatch({ control: methods.control, name: 'driverId' });

  // Get selected driver's store location (prioritized over filter store)
  const selectedDriverStore = useMemo(() => {
    if (selectedDriverId) {
      const driver = driversWithStoreData.find((d) => d.id === selectedDriverId);
      return driver?.store;
    }
    return undefined;
  }, [selectedDriverId, driversWithStoreData]);

  // Get selected store location - driver's store takes priority
  // When editing, use store from details.driver.store or details.store directly (has location data)
  const detailsStore = details?.driver?.store || details?.store;
  const selectedStore = selectedDriverStore ||
    storesWithLocation.find((s) => s.id === selectedStoreId) ||
    detailsStore ||
    (detailsStore?.id ? storesWithLocation.find((s) => s.id === detailsStore.id) : undefined);

  // Handle route planner order changes
  const handleRoutePlannerOrdersChange = useCallback((orders: ITripRoutePlannerOrder[]) => {
    setRoutePlannerOrders(orders);
    methods.setValue('orderIds', orders.map((o) => o.id));
  }, [methods]);

  // Handle adding order from route planner
  const handleRoutePlannerAddOrder = useCallback((order: IOrderDetails) => {
    // Order will be added via handleRoutePlannerOrdersChange
  }, []);

  // Handle removing order from route planner
  const handleRoutePlannerRemoveOrder = useCallback((orderId: number) => {
    // Order will be removed via handleRoutePlannerOrdersChange
  }, []);

  // Filter unassigned orders for route planner (exclude already assigned ones)
  const filteredUnassignedOrders = unassignedOrdersWithLocation.filter(
    (order) => !routePlannerOrders.some((ro) => ro.id === order.id)
  );

  const statusOptions = [
    { label: t("preparing"), value: TripStatus.PENDING },
    { label: t("delivery_in_progress"), value: TripStatus.ACTIVE },
    { label: t("completed"), value: TripStatus.COMPLETED },
    { label: t("canceled"), value: TripStatus.CANCELLED },
  ];

  const onSubmit = async (
    data: ITripRequest,
    shouldStart: boolean = false,
    statusOverride?: TripStatus,
    setLoadingOverride?: (loading: boolean) => void
  ) => {
    const setLoading =
      setLoadingOverride || (shouldStart ? setLoadingCreateAndStart : setLoadingSubmit);
    const submitData: ITripRequest = {
      ...data,
      ...(statusOverride ? { status: statusOverride } : {}),
    };

    if (submitData.status !== TripStatus.CANCELLED) {
      delete submitData.reason;
    } else if (!submitData.reason) {
      submitData.reason = "Cancelled from admin panel";
    }

    await execute({
      callback: async () => {
        setLoading(true);
        if (details) {
          await EndPoints.trip.updateTrip(details.id, submitData);
        } else {
          const response = await EndPoints.trip.createTrip(submitData);
          if (shouldStart && response.data) {
            // Immediately start the trip
            await EndPoints.trip.updateTrip(response.data.id, { status: 2 });
          }
        }
        navigate(-1);
      },
      fallback: () => { },
      finallyCallback: () => {
        setLoading(false);
      },
      throwException: false,
    });
  };

  return (
    <FormProvider {...methods}>
      <form className={styles.container}>
        <div>
          <span className={styles.title}>
            {details ? t("update_trip") : t("create_trip")}
          </span>
          <Divider className={styles.divider} />
          {loading.includes("details") && id ? (
            <div style={{ height: "60vh" }}>
              <Spin />
            </div>
          ) : (
            <div className={styles.children}>
              {/* Form Section */}
              <Card bordered={false}
                style={{ flexShrink: 0 }}
                extra={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: 12, padding: 10, color: '#888' }}>{t("refresh_dropdown_data")}</span>
                    <RefreshBtn
                      key={0}
                      onClick={() => refreshDropdownData()}
                      loading={refreshingData}
                      size="small"
                    >
                      {t("refresh")}
                    </RefreshBtn>
                  </div>
                }
              >
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={8} lg={8} xl={8} xxl={8}>
                    <FieldBuilder
                      name="filterStoreId"
                      label={t("store")}
                      input={{
                        type: "select",
                        options: stores,
                        loading: loadingStores,
                        onChange: (value: number) => {
                          setSelectedStoreId(value);
                          methods.setValue('driverId', undefined);
                        },
                      }}
                    />
                    <small style={{ color: "#888", marginTop: 4, display: 'block' }}>
                      {t("optional_filter_for_drivers")}
                    </small>
                  </Col>

                  <Col xs={24} sm={12} md={8} lg={8} xl={8} xxl={8}>
                    <FieldBuilder
                      name="filterClassificationId"
                      label={t("driver_classification")}
                      input={{
                        type: "select",
                        options: classifications,
                        loading: loadingClassifications,
                        onChange: (value: number) => {
                          setSelectedClassificationId(value);
                          methods.setValue('driverId', undefined);
                        },
                      }}
                    />
                    <small style={{ color: "#888", marginTop: 4, display: 'block' }}>
                      {t("optional_filter_for_drivers")}
                    </small>
                  </Col>

                  <Col xs={24} sm={24} md={8} lg={8} xl={8} xxl={8}>
                    <FieldBuilder
                      name="driverId"
                      label={t("driver")}
                      input={{
                        type: "select",
                        options: drivers,
                        loading: loadingDrivers,
                      }}
                      rules={{ required: true }}
                    />
                    {(selectedStoreId || selectedClassificationId) && (
                      <small style={{ color: "#3458a4", marginTop: 4, display: 'block' }}>
                        {t("filtered_by_store_or_classification")}
                      </small>
                    )}
                    {selectedDriverStore && (
                      <small style={{ color: "#52c41a", marginTop: 4, display: 'block', fontWeight: 500 }}>
                        📍 {t("start_point")}: {selectedDriverStore.name}
                      </small>
                    )}
                  </Col>

                  {details && (
                    <>
                      <Col xs={24} sm={12} md={8} lg={8} xl={8} xxl={8}>
                        <FieldBuilder
                          name="status"
                          label={t("status")}
                          input={{
                            type: "select",
                            options: statusOptions,
                            onChange: (value) => setSelectedStatus(value),
                          }}
                          rules={{ required: true }}
                        />
                      </Col>
                      {selectedStatus === TripStatus.CANCELLED && (
                        <Col xs={24} sm={24} md={16} lg={16} xl={16} xxl={16}>
                          <FieldBuilder
                            name="reason"
                            label={t("cancellation_reason")}
                            input={{
                              type: "text-area",
                              rows: 2,
                            }}
                            rules={{ required: true }}
                          />
                        </Col>
                      )}
                    </>
                  )}
                </Row>
              </Card>

              {/* Route Planner / Map Section */}
              <div style={{ marginTop: 16, flex: 1, minHeight: 600, display: 'flex', flexDirection: 'column' }}>
                <TripRoutePlanner
                  storeLocation={selectedStore?.location}
                  assignedOrders={routePlannerOrders}
                  unassignedOrders={filteredUnassignedOrders}
                  isEditable={!details || details.status === TripStatus.PENDING}
                  isLoading={loadingOrders}
                  mapPolylines={details?.mapPolylines}
                  routeStatus={
                    details?.status === TripStatus.ACTIVE
                      ? "active"
                      : details?.status === TripStatus.COMPLETED
                      ? "completed"
                      : "pending"
                  }
                  onOrdersChange={handleRoutePlannerOrdersChange}
                  onAddOrder={handleRoutePlannerAddOrder}
                  onRemoveOrder={handleRoutePlannerRemoveOrder}
                />
              </div>
              <Divider className={styles.divider} />
              <div className={styles.formFooter}>
                <Button onClick={() => navigate(-1)}>{t("cancel")}</Button>
                {details?.status === TripStatus.PENDING && (
                  <Button
                    loading={loadingStartTrip}
                    onClick={handleSubmit((data) =>
                      onSubmit(data, false, TripStatus.ACTIVE, setLoadingStartTrip)
                    )}
                    type="default"
                    style={{ borderColor: '#52c41a', color: '#52c41a' }}
                  >
                    {t("start_trip")}
                  </Button>
                )}
                {(details?.status === TripStatus.PENDING ||
                  details?.status === TripStatus.ACTIVE) && (
                    <Button
                      danger
                      loading={loadingCancelTrip}
                      onClick={handleSubmit((data) =>
                        onSubmit(data, false, TripStatus.CANCELLED, setLoadingCancelTrip)
                      )}
                    >
                      {t("cancel_trip")}
                    </Button>
                  )}
                {!details && (
                  <Button
                    loading={loadingCreateAndStart}
                    onClick={handleSubmit((data) => onSubmit(data, true))}
                    type="default"
                    style={{ borderColor: '#52c41a', color: '#52c41a' }}
                  >
                    {t("create_and_start")}
                  </Button>
                )}
                <Button loading={loadingSubmit} onClick={handleSubmit((data) => onSubmit(data, false))} type="primary">
                  {details ? t("save") : t("create")}
                </Button>
              </div>
            </div>
          )}
        </div>
      </form>
    </FormProvider>
  );
};

const TripFormPage = () => {
  return (
    <TripContextProvider>
      <Form />
    </TripContextProvider>
  );
};

export default TripFormPage;
